/**
 * A confirmação da conferência final e quem assina (E169.F1, GUS-113 e GUS-215, ADR-0117 §3).
 *
 * A confirmação vale para a versão lida: o servidor recusa a versão que mudou. Confirmada, o caso
 * passa a aguardar a assinatura. Quem é certificador segue para a assinatura; quem não é lê que ela
 * fica com o certificador da plataforma — a tela não oferece mais um link que acaba em acesso
 * restrito.
 */
import type { AxiosInstance } from 'axios';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Badge, Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import {
  ConferenciaFinalError,
  confirmarConferenciaFinal,
  saidasRevisadas,
  type ConferenciaFinalData,
} from '@/lib/conferencia_final';
import { textoDoMotivo } from '@/lib/proximoPasso';

import { RevogarCertificacao } from './RevogarCertificacao';

const t = ptBR.conferenciaFinal.assinatura;

export interface ConfirmarConferenciaProps {
  client: AxiosInstance;
  caseId: string;
  conferencia: ConferenciaFinalData;
  /** A confirmação gravou (ou a versão mudou): a conferência relê. */
  onConfirmada: () => void;
}

function QuemAssina({ conferencia }: { conferencia: ConferenciaFinalData }) {
  const motivo = conferencia.sem_assinar;
  return (
    <p className="text-body-sm text-fg-muted" data-testid="quem-assina">
      {t.quem} {motivo ? t.semAssinar[motivo] : t.voceAssina}
    </p>
  );
}

function Segura({ conferencia }: { conferencia: ConferenciaFinalData }) {
  const motivo = conferencia.assinatura.motivo;
  if (conferencia.assinatura.liberada || !motivo) return null;
  return (
    <p className="text-body-sm text-warning-700" data-testid="assinatura-segura">
      {t.segura.replace('{motivo}', textoDoMotivo(motivo, conferencia.assinatura.pendentes))}
    </p>
  );
}

function Formulario({
  conferencia,
  pronta,
  enviando,
  onConfirmar,
}: {
  conferencia: ConferenciaFinalData;
  pronta: boolean;
  enviando: boolean;
  onConfirmar: () => void;
}) {
  const [confirmo, setConfirmo] = useState(false);
  const rotulo = conferencia.pode_assinar ? t.conferirEAssinar : t.conferirEEnviar;
  return (
    <>
      <label className="flex items-start gap-sc-2 text-body-sm text-fg">
        <input
          type="checkbox"
          checked={confirmo}
          disabled={!pronta || enviando}
          onChange={(e) => setConfirmo(e.target.checked)}
          data-testid="confirmo"
        />
        <span>{t.confirmo}</span>
      </label>
      <Button
        type="button"
        className="self-start"
        loading={enviando}
        disabled={!pronta || !confirmo}
        onClick={onConfirmar}
        data-testid="confirmar"
      >
        {enviando ? t.confirmando : rotulo}
      </Button>
    </>
  );
}

function Mensagens({ aviso, erro }: { aviso: string | null; erro: string | null }) {
  return (
    <>
      {aviso !== null ? (
        <p role="status" className="text-body-sm text-success-700" data-testid="aviso-aguardando">
          {aviso}
        </p>
      ) : null}
      {erro !== null ? (
        <p role="alert" className="text-body-sm text-danger-700">
          {erro}
        </p>
      ) : null}
    </>
  );
}

export function ConfirmarConferencia({
  client,
  caseId,
  conferencia,
  onConfirmada,
}: ConfirmarConferenciaProps) {
  const navigate = useNavigate();
  const [enviando, setEnviando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const pronta = conferencia.assinatura.liberada && saidasRevisadas(conferencia);
  const assinada = conferencia.requisicao === 'laudo_entregue';

  async function confirmar(): Promise<void> {
    setEnviando(true);
    setErro(null);
    try {
      const feita = await confirmarConferenciaFinal(client, caseId, conferencia);
      if (feita.pode_assinar) {
        navigate(`/certificacao?requisicao=${feita.requisicao_id}`);
        return;
      }
      setAviso(t.naFila);
      onConfirmada();
    } catch (err) {
      const motivo = err instanceof ConferenciaFinalError ? err.motivo : 'indisponivel';
      setErro(ptBR.conferenciaFinal.recusa[motivo]);
      if (motivo === 'versao_desatualizada') onConfirmada();
    } finally {
      setEnviando(false);
    }
  }

  return (
    <section
      className="flex flex-col gap-sc-3 border border-border bg-surface p-sc-4"
      aria-labelledby="assinatura-da-versao"
      data-testid="confirmar-conferencia"
    >
      <div className="flex flex-wrap items-center justify-between gap-sc-2">
        <h2 id="assinatura-da-versao" className="text-body-lg font-semibold text-fg">
          {t.titulo}
        </h2>
        {conferencia.requisicao ? (
          <Badge variant={assinada ? 'success' : 'warning'} data-testid="situacao-da-assinatura">
            {t.requisicao[conferencia.requisicao]}
          </Badge>
        ) : null}
      </div>
      <QuemAssina conferencia={conferencia} />
      <Segura conferencia={conferencia} />
      {!assinada ? (
        <Formulario
          conferencia={conferencia}
          pronta={pronta}
          enviando={enviando}
          onConfirmar={() => void confirmar()}
        />
      ) : null}
      {assinada && conferencia.requisicao_id ? (
        <RevogarCertificacao
          client={client}
          requisicaoId={conferencia.requisicao_id}
          onRevogada={() => {
            setAviso(ptBR.conferenciaFinal.revogacao.revogada);
            onConfirmada();
          }}
        />
      ) : null}
      <Mensagens aviso={aviso} erro={erro} />
    </section>
  );
}
